import React, { useState, useEffect, useMemo, useRef } from 'react';
import withEditRights from '../hoc/with-edit-rights';
import apiClient from '../../util/api-client';
import styles from './AlumniPage.module.css';

const CATEGORIES = ['Alla', 'Tävling', 'Firande', 'Träning', 'Gemenskap'];

const AlumniPage = ({ canEdit }) => {
  const [photos, setPhotos] = useState(() => {
    try {
      const local = localStorage.getItem('alumniPhotos');
      if (local) {
        const parsed = JSON.parse(local);
        // Filtrera bort gamla demo/starter-bilder så albumet är helt tomt från början
        if (Array.isArray(parsed)) {
          const userOnly = parsed.filter((p) => p && !String(p.id).startsWith('starter-'));
          return userOnly;
        }
      }
    } catch (e) {
      console.warn('Could not read alumniPhotos from local storage', e);
    }
    return [];
  });

  const [selectedYear, setSelectedYear] = useState('ALL');
  const [selectedCategory, setSelectedCategory] = useState('Alla');
  const [searchQuery, setSearchQuery] = useState('');

  // Lightbox
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Add photo modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newPhotoUrl, setNewPhotoUrl] = useState('');
  const [newPhotoCaption, setNewPhotoCaption] = useState('');
  const [newPhotoYear, setNewPhotoYear] = useState(new Date().getFullYear().toString());
  const [newPhotoCategory, setNewPhotoCategory] = useState('Tävling');
  const [previewError, setPreviewError] = useState(false);
  const fileInputRef = useRef(null);

  // Synka med MongoDB Atlas vid laddning
  useEffect(() => {
    let isMounted = true;
    apiClient.getAlumniPhotos().then((data) => {
      if (!isMounted) return;
      if (Array.isArray(data)) {
        // Behåll enbart användarens egna uppladdade bilder
        const userPhotos = data.filter((p) => p && !String(p.id).startsWith('starter-'));
        setPhotos(userPhotos);
        localStorage.setItem('alumniPhotos', JSON.stringify(userPhotos));

        // Rensa bort gamla demobilder om de fanns i MongoDB
        if (userPhotos.length !== data.length) {
          apiClient.saveAlumniPhotos(userPhotos);
        }
      } else {
        setPhotos([]);
        localStorage.setItem('alumniPhotos', JSON.stringify([]));
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowRight') handleNextPhoto();
      if (e.key === 'ArrowLeft') handlePrevPhoto();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, photos]);

  // Dynamiska år från bilderna
  const availableYears = useMemo(() => {
    const set = new Set();
    photos.forEach((p) => {
      if (p.year) set.add(String(p.year));
    });
    return Array.from(set).sort((a, b) => Number(b) - Number(a));
  }, [photos]);

  // Filtrerade bilder
  const filteredPhotos = useMemo(() => {
    return photos.filter((p) => {
      const matchYear = selectedYear === 'ALL' || String(p.year) === String(selectedYear);
      const matchCategory =
        selectedCategory === 'Alla' ||
        (p.category && p.category.toLowerCase() === selectedCategory.toLowerCase());
      const matchSearch =
        !searchQuery.trim() ||
        (p.caption && p.caption.toLowerCase().includes(searchQuery.toLowerCase().trim())) ||
        (p.year && String(p.year).includes(searchQuery.trim()));

      return matchYear && matchCategory && matchSearch;
    });
  }, [photos, selectedYear, selectedCategory, searchQuery]);

  // Bildkomprimering via HTML Canvas
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_SIZE = 950;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_SIZE) {
              height = Math.round((height * MAX_SIZE) / width);
              width = MAX_SIZE;
            }
          } else {
            if (height > MAX_SIZE) {
              width = Math.round((width * MAX_SIZE) / height);
              height = MAX_SIZE;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
          resolve(dataUrl);
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    try {
      const compressedDataUrl = await compressImage(file);
      setNewPhotoUrl(compressedDataUrl);
      setPreviewError(false);
    } catch (err) {
      console.error('Fel vid bildinläsning:', err);
      alert('Kunde inte läsa in bilden. Försök med en annan bildfil.');
    }
  };

  const handleSaveNewPhoto = async () => {
    if (!newPhotoUrl.trim() || !newPhotoCaption.trim()) {
      alert('Vänligen fyll i både bild och en beskrivning.');
      return;
    }

    const newPhoto = {
      id: 'photo-' + Date.now(),
      url: newPhotoUrl.trim(),
      caption: newPhotoCaption.trim(),
      year: newPhotoYear || new Date().getFullYear().toString(),
      category: newPhotoCategory || 'Tävling',
      createdAt: new Date().toISOString(),
    };

    const updated = [newPhoto, ...photos];
    setPhotos(updated);
    apiClient.saveAlumniPhotos(updated);

    // Återställ formulär
    setNewPhotoUrl('');
    setNewPhotoCaption('');
    setNewPhotoYear(new Date().getFullYear().toString());
    setNewPhotoCategory('Tävling');
    setIsModalOpen(false);
  };

  const handleDeletePhoto = (id, e) => {
    e.stopPropagation();
    if (window.confirm('Är du säker på att du vill ta bort detta minne från albumet?')) {
      const updated = photos.filter((p) => p.id !== id);
      setPhotos(updated);
      apiClient.saveAlumniPhotos(updated);
      if (lightboxIndex !== null) {
        setLightboxIndex(null);
      }
    }
  };

  const handleNextPhoto = () => {
    if (filteredPhotos.length === 0) return;
    setLightboxIndex((prev) => (prev + 1) % filteredPhotos.length);
  };

  const handlePrevPhoto = () => {
    if (filteredPhotos.length === 0) return;
    setLightboxIndex((prev) => (prev - 1 + filteredPhotos.length) % filteredPhotos.length);
  };

  const currentLightboxPhoto =
    lightboxIndex !== null && filteredPhotos[lightboxIndex]
      ? filteredPhotos[lightboxIndex]
      : null;

  return (
    <div className={styles.pageContainer}>
      {/* Hero Section */}
      <div className={styles.heroSection}>
        <div className={styles.heroBackground} />
        <div className={styles.heroGlow1} />
        <div className={styles.heroGlow2} />

        <div className={styles.heroContent}>
          <div className={styles.heroBadge}>
            <span className={styles.badgePulse} />
            📸 HEALTHWEEK MINNESALBUM
          </div>
          <h1 className={styles.heroTitle}>Alumni & Galleri</h1>
          <p className={styles.heroSubtitle}>
            Fångade ögonblick, firanden och gemenskap från alla HealthWeeks genom tiderna
          </p>

          {/* Statistik-piller */}
          <div className={styles.statsBar}>
            <div className={styles.statPill}>
              <span className={styles.statNumber}>{photos.length}</span>
              <span className={styles.statLabel}>Foton & Minnen</span>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.statPill}>
              <span className={styles.statNumber}>{availableYears.length}</span>
              <span className={styles.statLabel}>Årgångar</span>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.statPill}>
              <span className={styles.statNumber}>100%</span>
              <span className={styles.statLabel}>Gemenskap</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className={styles.mainWrapper}>
        {/* Kontrollpanel: Filter & Sök & Lägg till */}
        <div className={styles.controlPanel}>
          <div className={styles.filterGroup}>
            {/* Årsfilter (visas om det finns bilder) */}
            {availableYears.length > 0 && (
              <div className={styles.yearFilters}>
                <button
                  className={`${styles.filterPill} ${selectedYear === 'ALL' ? styles.activePill : ''}`}
                  onClick={() => setSelectedYear('ALL')}
                >
                  Alla år
                </button>
                {availableYears.map((yr) => (
                  <button
                    key={yr}
                    className={`${styles.filterPill} ${selectedYear === yr ? styles.activePill : ''}`}
                    onClick={() => setSelectedYear(yr)}
                  >
                    {yr}
                  </button>
                ))}
              </div>
            )}

            {/* Kategorifilter (visas om det finns bilder) */}
            {photos.length > 0 && (
              <div className={styles.categoryFilters}>
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    className={`${styles.categoryPill} ${selectedCategory === cat ? styles.activeCategory : ''}`}
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {cat === 'Tävling' && '🏆 '}
                    {cat === 'Firande' && '🎉 '}
                    {cat === 'Träning' && '💪 '}
                    {cat === 'Gemenskap' && '👥 '}
                    {cat}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className={styles.actionGroup}>
            {/* Sökfält (visas om det finns bilder) */}
            {photos.length > 0 && (
              <div className={styles.searchBox}>
                <span className={styles.searchIcon}>🔍</span>
                <input
                  type="text"
                  placeholder="Sök minnen..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={styles.searchInput}
                />
                {searchQuery && (
                  <button
                    className={styles.clearSearchBtn}
                    onClick={() => setSearchQuery('')}
                    title="Rensa sökning"
                  >
                    ✕
                  </button>
                )}
              </div>
            )}

            {/* Lägg till knapp för lärare */}
            {canEdit && (
              <button
                className={styles.addMemoryButton}
                onClick={() => setIsModalOpen(true)}
              >
                <span className={styles.plusIcon}>+</span> Lägg till minne
              </button>
            )}
          </div>
        </div>

        {/* Info-rad: Visar antal matchande minnen om det finns bilder */}
        {photos.length > 0 && (
          <div className={styles.galleryMetaRow}>
            <span className={styles.galleryCount}>
              Visar <strong>{filteredPhotos.length}</strong> minnen
              {selectedYear !== 'ALL' && ` för år ${selectedYear}`}
              {selectedCategory !== 'Alla' && ` inom ${selectedCategory}`}
            </span>
            {canEdit && (
              <span className={styles.teacherTip}>
                ✨ Du är inloggad som lärare och kan ladda upp och radera bilder.
              </span>
            )}
          </div>
        )}

        {/* FOTO-GALLERI ELLER TOM-VY */}
        {photos.length === 0 ? (
          <div className={styles.emptyContainer}>
            <div className={styles.emptyIcon}>📸</div>
            <h3 style={{ fontSize: '1.6rem', marginBottom: '0.75rem', color: '#fff' }}>
              Albumet är tomt
            </h3>
            <p style={{ color: '#94a3b8', maxWidth: '500px', margin: '0 auto 1.5rem', lineHeight: '1.6' }}>
              {canEdit
                ? 'Det finns inga bilder i albumet än. Som lärare kan du ladda upp dina egna foton och bevara minnen från HealthWeek här!'
                : 'Det finns inga bilder i albumet än. Håll utkik när lärare laddar upp foton och minnen från tävlingarna!'}
            </p>
            {canEdit && (
              <button
                className={styles.addMemoryButton}
                onClick={() => setIsModalOpen(true)}
                style={{ margin: '0 auto', padding: '0.85rem 1.8rem', fontSize: '1.05rem' }}
              >
                <span className={styles.plusIcon}>+</span> Lägg till första bilden
              </button>
            )}
          </div>
        ) : filteredPhotos.length === 0 ? (
          <div className={styles.emptyContainer}>
            <div className={styles.emptyIcon}>🔍</div>
            <h3>Inga foton matchade sökningen</h3>
            <p>Prova att välja ett annat år eller rensa filtren.</p>
            <button
              className={styles.resetFilterBtn}
              onClick={() => {
                setSelectedYear('ALL');
                setSelectedCategory('Alla');
                setSearchQuery('');
              }}
            >
              Återställ alla filter
            </button>
          </div>
        ) : (
          <div className={styles.photoGrid}>
            {filteredPhotos.map((photo, index) => (
              <div
                key={photo.id || index}
                className={styles.photoCard}
                onClick={() => setLightboxIndex(index)}
              >
                <div className={styles.cardImageWrapper}>
                  <img
                    src={photo.url}
                    alt={photo.caption || 'HealthWeek minne'}
                    className={styles.cardImage}
                    loading="lazy"
                  />
                  <div className={styles.cardGradientOverlay} />

                  {/* Årtal & Kategori-bricka */}
                  <div className={styles.cardBadges}>
                    {photo.year && <span className={styles.yearBadge}>{photo.year}</span>}
                    {photo.category && (
                      <span className={styles.categoryBadge}>{photo.category}</span>
                    )}
                  </div>

                  {/* Radera-knapp för lärare */}
                  {canEdit && (
                    <button
                      className={styles.deleteCardBtn}
                      onClick={(e) => handleDeletePhoto(photo.id, e)}
                      title="Ta bort detta minne"
                    >
                      🗑️
                    </button>
                  )}

                  {/* Textinfo nertill */}
                  <div className={styles.cardFooter}>
                    <h3 className={styles.cardCaption}>{photo.caption}</h3>
                    <div className={styles.cardMeta}>
                      <span className={styles.zoomHint}>Klicka för helskärm ↗</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* LIGHTBOX MODAL */}
      {currentLightboxPhoto && (
        <div
          className={styles.lightboxOverlay}
          onClick={(e) => {
            if (e.target === e.currentTarget) setLightboxIndex(null);
          }}
        >
          <button
            className={styles.lightboxCloseBtn}
            onClick={() => setLightboxIndex(null)}
            title="Stäng (Esc)"
          >
            ✕
          </button>

          {filteredPhotos.length > 1 && (
            <>
              <button
                className={`${styles.navArrow} ${styles.navArrowLeft}`}
                onClick={handlePrevPhoto}
                title="Föregående bild (Vänsterpil)"
              >
                ‹
              </button>
              <button
                className={`${styles.navArrow} ${styles.navArrowRight}`}
                onClick={handleNextPhoto}
                title="Nästa bild (Högerpil)"
              >
                ›
              </button>
            </>
          )}

          <div className={styles.lightboxContent}>
            <div className={styles.lightboxImageContainer}>
              <img
                src={currentLightboxPhoto.url}
                alt={currentLightboxPhoto.caption}
                className={styles.lightboxImage}
              />
            </div>

            <div className={styles.lightboxInfoPanel}>
              <div className={styles.lightboxMetaRow}>
                {currentLightboxPhoto.year && (
                  <span className={styles.lightboxYearBadge}>
                    {currentLightboxPhoto.year}
                  </span>
                )}
                {currentLightboxPhoto.category && (
                  <span className={styles.lightboxCategoryBadge}>
                    {currentLightboxPhoto.category}
                  </span>
                )}
                {filteredPhotos.length > 1 && (
                  <span className={styles.lightboxCounter}>
                    {lightboxIndex + 1} av {filteredPhotos.length}
                  </span>
                )}
              </div>
              <h2 className={styles.lightboxCaption}>
                {currentLightboxPhoto.caption}
              </h2>
            </div>
          </div>
        </div>
      )}

      {/* LÄGG TILL MINNE MODAL (LÄRARE / ADMIN) */}
      {isModalOpen && canEdit && (
        <div
          className={styles.modalOverlay}
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsModalOpen(false);
          }}
        >
          <div className={styles.modalCard}>
            <div className={styles.modalHeader}>
              <div>
                <h2 className={styles.modalTitle}>Lägg till nytt minne</h2>
                <p className={styles.modalSubtitle}>
                  Ladda upp en egen bild och bevara ett ögonblick i HealthWeek-arkivet
                </p>
              </div>
              <button
                className={styles.modalCloseBtn}
                onClick={() => setIsModalOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className={styles.modalBody}>
              {/* Filuppladdning eller URL */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Välj bild</label>
                <div className={styles.uploadOptions}>
                  <button
                    type="button"
                    className={styles.filePickerButton}
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  >
                    📁 Välj bild från enheten
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                  <span className={styles.orText}>eller klistra in URL:</span>
                </div>
                <input
                  type="text"
                  className={styles.textInput}
                  placeholder="https://... eller klistra in bildlänk"
                  value={newPhotoUrl}
                  onChange={(e) => {
                    setNewPhotoUrl(e.target.value);
                    setPreviewError(false);
                  }}
                />
              </div>

              {/* Bildförhandsgranskning */}
              {newPhotoUrl && (
                <div className={styles.previewContainer}>
                  <div className={styles.previewLabel}>Förhandsgranskning:</div>
                  <div className={styles.previewBox}>
                    <img
                      src={newPhotoUrl}
                      alt="Förhandsvisning"
                      className={styles.previewImg}
                      onError={() => setPreviewError(true)}
                    />
                    {previewError && (
                      <div className={styles.previewErrorOverlay}>
                        ⚠️ Kunde inte ladda bilden från länken. Kontrollera adressen.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Beskrivning */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Beskrivning / Bildtext</label>
                <input
                  type="text"
                  className={styles.textInput}
                  placeholder="T.ex. Fotbollsfinalen 2026..."
                  value={newPhotoCaption}
                  onChange={(e) => setNewPhotoCaption(e.target.value)}
                />
              </div>

              {/* År och Kategori */}
              <div className={styles.formRow}>
                <div className={styles.formGroupHalf}>
                  <label className={styles.formLabel}>Årtal</label>
                  <input
                    type="number"
                    className={styles.textInput}
                    placeholder="2026"
                    value={newPhotoYear}
                    onChange={(e) => setNewPhotoYear(e.target.value)}
                  />
                </div>
                <div className={styles.formGroupHalf}>
                  <label className={styles.formLabel}>Kategori</label>
                  <select
                    className={styles.selectInput}
                    value={newPhotoCategory}
                    onChange={(e) => setNewPhotoCategory(e.target.value)}
                  >
                    <option value="Tävling">🏆 Tävling</option>
                    <option value="Firande">🎉 Firande</option>
                    <option value="Träning">💪 Träning</option>
                    <option value="Gemenskap">👥 Gemenskap</option>
                  </select>
                </div>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => setIsModalOpen(false)}
              >
                Avbryt
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleSaveNewPhoto}
                disabled={!newPhotoUrl.trim() || !newPhotoCaption.trim()}
              >
                ✨ Spara till arkivet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default withEditRights(AlumniPage);
